const TIMEZONE = "Europe/Kyiv";

export function kyivDate(date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function kyivTime(date = new Date()): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function formatKyivDate(date = new Date()): string {
  return new Intl.DateTimeFormat("uk-UA", {
    timeZone: TIMEZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
}

export function isPastTime(hhmm: string, now = new Date()): boolean {
  return kyivTime(now) >= hhmm;
}

export function formatSavedAt(iso: string): string {
  return new Intl.DateTimeFormat("uk-UA", {
    timeZone: TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

export function formatPostDay(iso: string): string {
  const day = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
  const today = kyivDate();
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = kyivDate(yesterdayDate);
  if (day === today) return "Сьогодні";
  if (day === yesterday) return "Вчора";
  return new Intl.DateTimeFormat("uk-UA", {
    timeZone: TIMEZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(iso));
}

export function formatPostTime(iso: string): string {
  return formatSavedAt(iso);
}

export function formatPhotoCredit(iso: string, authorName = ""): string {
  const who = authorName.trim();
  if (!iso) return who;
  const when = `${formatPostDay(iso)}, ${formatPostTime(iso)}`;
  return who ? `${when} · ${who}` : when;
}

const WEEKDAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

export function addYmd(ymd: string, days: number): string {
  const date = ymdToUtcNoon(ymd);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function ymdToUtcNoon(ymd: string): Date {
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12));
}

export function kyivWeekdayIndex(date = new Date()): number {
  const label = new Intl.DateTimeFormat("en-US", {
    timeZone: TIMEZONE,
    weekday: "long",
  }).format(date);
  return WEEKDAY_LONG.indexOf(label as (typeof WEEKDAY_LONG)[number]);
}

export function isWeekendYmd(ymd: string): boolean {
  const index = kyivWeekdayIndex(ymdToUtcNoon(ymd));
  return index === 0 || index === 6;
}

export function formatYmdUk(ymd: string): string {
  return new Intl.DateTimeFormat("uk-UA", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
  }).format(ymdToUtcNoon(ymd));
}

export function formatYmdLong(ymd: string): string {
  return new Intl.DateTimeFormat("uk-UA", {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(ymdToUtcNoon(ymd));
}

export function weekdayUk(ymd: string): string {
  return new Intl.DateTimeFormat("uk-UA", {
    timeZone: "UTC",
    weekday: "long",
  }).format(ymdToUtcNoon(ymd));
}

export function weekMondayYmd(ymd: string): string {
  const weekday = kyivWeekdayIndex(ymdToUtcNoon(ymd));
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  return addYmd(ymd, mondayOffset);
}

export function schoolWeekRange(ymd: string): { from: string; to: string } {
  const from = weekMondayYmd(ymd);
  return { from, to: addYmd(from, 4) };
}

export function formatYmdRange(from: string, to: string): string {
  const start = ymdToUtcNoon(from);
  const end = ymdToUtcNoon(to);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  const sameMonth = sameYear && from.slice(5, 7) === to.slice(5, 7);
  const startDay = new Intl.DateTimeFormat("uk-UA", { timeZone: "UTC", day: "numeric" }).format(start);
  const endLabel = new Intl.DateTimeFormat("uk-UA", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(end);
  if (sameMonth) return `${startDay}–${endLabel}`;
  const startLabel = new Intl.DateTimeFormat("uk-UA", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(start);
  return `${startLabel} – ${endLabel}`;
}

export function isHhmm(value: string): boolean {
  return /^\d{2}:\d{2}$/.test(value);
}

export function addMinutesHhmm(hhmm: string, minutes: number): string {
  const [hours, mins] = hhmm.split(":").map(Number);
  const total = ((hours * 60 + mins + minutes) % (24 * 60) + 24 * 60) % (24 * 60);
  const nextHours = Math.floor(total / 60);
  const nextMins = total % 60;
  return `${String(nextHours).padStart(2, "0")}:${String(nextMins).padStart(2, "0")}`;
}

const MONTH_DAY = /^\d{2}-\d{2}$/;
const FULL_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function normalizeBirthday(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (FULL_DAY.test(trimmed)) return trimmed.slice(5);
  if (MONTH_DAY.test(trimmed)) return trimmed;
  return "";
}

/** Only for <input type="date">. Never stored. 2020 is a leap year so 29 лютого теж відкриється. */
export function birthdayInputValue(value: string): string {
  const monthDay = normalizeBirthday(value);
  return monthDay ? `2020-${monthDay}` : "";
}

export function formatBirthdayUk(value: string): string {
  const monthDay = normalizeBirthday(value);
  if (!monthDay) return "";
  return formatYmdUk(`2020-${monthDay}`);
}

/** Days until the next occurrence of MM-DD from a Kyiv calendar day. 0 = today. */
export function daysUntilNextBirthday(birthday: string, fromYmd = kyivDate()): number | null {
  const monthDay = normalizeBirthday(birthday);
  if (!monthDay) return null;
  const year = Number(fromYmd.slice(0, 4));
  const from = ymdToUtcNoon(fromYmd);
  let next = ymdToUtcNoon(`${year}-${monthDay}`);
  if (next.getTime() < from.getTime()) {
    next = ymdToUtcNoon(`${year + 1}-${monthDay}`);
  }
  return Math.round((next.getTime() - from.getTime()) / 86_400_000);
}

export function weekendLabel(ymd: string): string {
  return kyivWeekdayIndex(ymdToUtcNoon(ymd)) === 0 ? "неділя" : "субота";
}
